import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Seo } from "@/components/Seo";
import { ShoppingBag, MapPin, ExternalLink, Plus, Zap, ArrowRight } from "lucide-react";
import TiltCard from "@/components/hud/TiltCard";
import ParticleField from "@/components/hud/ParticleField";

export default function Storefront() {
  const [data, setData] = useState({ products: [], print_partner: null });
  const [filter, setFilter] = useState("all");
  const { add, count } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/shop/products").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const products = data.products.filter((p) => {
    if (filter === "all") return true;
    if (filter === "amazon") return p.amazon?.listed;
    const t = p.type.toLowerCase();
    if (filter === "tee") return t.includes("tee") || t.includes("shirt");
    return t.includes(filter);
  });

  return (
    <div className="min-h-screen bg-app relative pt-12 pb-28" data-testid="storefront-page">
      <Seo
        title="Shop — Sci-Fi Print-on-Demand Apparel"
        description="Shop AI-designed sci-fi apparel — premium print-on-demand t-shirts, hoodies & more. Customize your own front & back designs in the AETHER design studio."
        path="/shop"
      />
      <div className="absolute inset-0 hud-grid opacity-20" />
      <div className="relative mx-auto max-w-7xl px-5">
        {/* header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-3">
          <div>
            <div className="font-mono text-[11px] tracking-[0.4em] text-cyan/70">SUPPLY DEPOT</div>
            <h1 className="font-head text-4xl sm:text-5xl font-bold uppercase mt-2">
              AETHER <span className="text-amber text-glow-amber">Apparel</span>
            </h1>
          </div>
          <button
            onClick={() => navigate("/shop/cart")}
            data-testid="storefront-cart-btn"
            className="btn-hud relative flex items-center gap-2 border border-cyan/40 text-cyan px-5 py-2.5 text-sm hover:bg-cyan hover:text-app"
          >
            <ShoppingBag className="w-4 h-4" /> Cart
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-amber text-app font-mono text-[10px] w-5 h-5 flex items-center justify-center rounded-full" data-testid="cart-count">
                {count}
              </span>
            )}
          </button>
        </div>

        {data.print_partner && (
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-slate-500 mb-8">
            <MapPin className="w-3.5 h-3.5 text-cyan" />
            PRINT-ON-DEMAND · {data.print_partner.name?.toUpperCase()} · {data.print_partner.city?.toUpperCase()}, {data.print_partner.country?.toUpperCase()}
          </div>
        )}

        {/* Dynamic featured drop banner */}
        {data.products[0] && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="relative overflow-hidden border border-cyan/20 mb-10 clip-chamfer"
            data-testid="featured-banner"
            style={{ minHeight: 260 }}
          >
            <div className="absolute inset-0 hud-grid opacity-30" />
            <div className="absolute inset-0 opacity-60"><ParticleField color="0,229,255" density={36} /></div>
            <div className="relative grid md:grid-cols-2 gap-6 items-center p-7 md:p-9">
              <div>
                <div className="font-mono text-[10px] tracking-[0.4em] text-amber animate-flicker">// LATEST DROP</div>
                <h2 className="font-head text-3xl sm:text-4xl font-bold uppercase mt-3 leading-tight">
                  Wear the <span className="text-cyan text-glow-cyan">Simulation</span>
                </h2>
                <p className="text-slate-400 mt-3 max-w-md">{data.products[0].description}</p>
                <Link
                  to="/simulation"
                  data-testid="featured-cta"
                  className="btn-hud inline-flex items-center gap-2 mt-6 border border-cyan text-cyan px-6 py-3 text-sm box-glow-cyan hover:bg-cyan hover:text-app"
                >
                  Explore Simulations <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/studio"
                  data-testid="featured-studio-cta"
                  className="ml-3 btn-hud inline-flex items-center gap-2 mt-6 border border-amber/50 text-amber px-5 py-3 text-sm hover:bg-amber hover:text-app"
                >
                  <Zap className="w-4 h-4" /> Design Your Own
                </Link>
                <Link
                  to={`/shop/product/${data.products[0].product_id}`}
                  className="ml-3 inline-flex items-center font-mono text-[11px] tracking-widest text-slate-400 hover:text-amber mt-6"
                >
                  INSPECT {data.products[0].name?.toUpperCase()} →
                </Link>
              </div>
              <div className="relative h-52 md:h-60">
                <TiltCard className="w-full h-full" max={12}>
                  <div className="relative w-full h-full overflow-hidden border border-cyan/25 brackets">
                    <img src={data.products[0].image} alt={data.products[0].name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-tr from-app/40 to-transparent" />
                  </div>
                </TiltCard>
              </div>
            </div>
          </motion.div>
        )}

        {/* filters */}
        <div className="flex gap-2 mb-8" data-testid="shop-filters">
          {[["all", "All"], ["tee", "Tees"], ["hoodie", "Hoodies"], ["amazon", "On Amazon"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`filter-${k}`}
              className={`font-head text-xs uppercase tracking-[0.15em] px-4 py-2 border transition-colors ${
                filter === k ? "border-cyan text-cyan box-glow-cyan" : "border-white/10 text-slate-400 hover:border-cyan/50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="product-grid">
          {products.map((p, i) => (
            <motion.div
              key={p.product_id}
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (i % 3) * 0.08 }}
              data-testid={`product-card-${p.product_id}`}
            >
              <TiltCard className="group glass clip-chamfer border border-cyan/15 hover:border-cyan/50 transition-colors overflow-hidden flex flex-col h-full">
              <Link to={`/shop/product/${p.product_id}`} className="relative aspect-square overflow-hidden bg-black/40 block">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                {p.amazon?.listed && (
                  <div className="absolute top-3 left-3 font-mono text-[9px] tracking-widest px-2 py-1 bg-app/80 border border-amber/40 text-amber">
                    AMAZON
                  </div>
                )}
                <div className="absolute top-3 right-3 font-mono text-[9px] tracking-widest px-2 py-1 bg-app/80 border" style={{ color: p.accent, borderColor: p.accent + "55" }}>
                  {p.type}
                </div>
              </Link>
              <div className="p-5 flex flex-col flex-1">
                <Link to={`/shop/product/${p.product_id}`} className="font-head text-lg uppercase tracking-wide text-slate-100 hover:text-cyan transition-colors">
                  {p.name}
                </Link>
                <p className="text-sm text-slate-400 mt-2 line-clamp-2 flex-1">{p.description}</p>
                <div className="flex items-center justify-between mt-4">
                  <span className="font-head text-2xl font-bold" style={{ color: p.accent }}>${p.price}</span>
                  <button
                    onClick={() => add(p, "M", 1)}
                    data-testid={`quick-add-${p.product_id}`}
                    className="btn-hud flex items-center gap-1.5 border border-cyan/40 text-cyan px-3 py-2 text-xs hover:bg-cyan hover:text-app"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add
                  </button>
                </div>
                {p.amazon_url && (
                  <a href={p.amazon_url} target="_blank" rel="noreferrer" className="mt-3 flex items-center gap-1 font-mono text-[10px] text-slate-500 hover:text-amber">
                    <ExternalLink className="w-3 h-3" /> VIEW ON AMAZON CHANNEL
                  </a>
                )}
              </div>
              </TiltCard>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
